import {
  type Coords,
  type DoneCallback,
  type LeafletEvent,
  TileLayer,
  type TileLayerOptions,
} from "leaflet";
import {
  createElementObject,
  createTileLayerComponent,
  updateGridLayer,
  withPane,
} from "@react-leaflet/core";
import { type TileLayerProps } from "react-leaflet";
import React, { useCallback, useRef } from "react";
import { fetchZenrinTile } from "@/features/map/api/fetchZenrinTile";
import { EMPTY_PIXEL } from "@/features/map/constants";
import { useZenrinOAuthToken } from "../hooks/useZenrinOAuthToken";
import { recordZenrinTileFailure } from "../utils/zenrinTileFailureWindow";

export const ZENRIN_RESIDENTIAL_MIN_ZOOM = 7;
export const ZENRIN_RESIDENTIAL_MAX_ZOOM = 22;
export const ZENRIN_RESIDENTIAL_MAP_LAYER_NAME = "住宅地図（表札なし）";

/**
 * タイルレイヤー生成処理＋HTTPヘッダー付与
 * 下記URLのコードを参考に実装
 * https://github.com/jaq316/leaflet-header
 */
export class TileLayerWithHeaders extends TileLayer {
  private readonly headers:
    | Array<{ header: string; value: string }>
    | undefined;

  private oauthToken?: string;
  private onTokenInvalid?: () => void;

  constructor(
    url: string,
    options?: TileLayerOptions,
    headers?: Array<{ header: string; value: string }>,
    oauthToken?: string,
    onTokenInvalid?: () => void
  ) {
    super(url, options);
    this.headers = headers;
    this.oauthToken = oauthToken;
    this.onTokenInvalid = onTokenInvalid;
  }

  setOAuthToken(token: string | undefined): void {
    this.oauthToken = token;
    this.redraw();
  }

  setOnTokenInvalid(callback: (() => void) | undefined): void {
    this.onTokenInvalid = callback;
  }

  protected createTile(coords: Coords, done: DoneCallback): HTMLImageElement {
    const url = this.getTileUrl(coords);
    const img = document.createElement("img");
    img.setAttribute("role", "presentation");

    const controller = new AbortController();

    const onTileUnload = (e: LeafletEvent & { tile?: HTMLElement }): void => {
      if (e.tile === img) {
        controller.abort();
      }
    };
    this.on("tileunload", onTileUnload);

    void (async (): Promise<void> => {
      try {
        const result = await fetchZenrinTile(url, {
          signal: controller.signal,
          headers: this.headers,
          oauthToken: this.oauthToken,
          onTokenInvalid: this.onTokenInvalid,
        });

        if (result.kind === "success") {
          img.src = result.objectUrl;
          done(undefined, img);
          return;
        }

        if (result.kind === "aborted") {
          img.src = EMPTY_PIXEL;
          done(undefined, img);
          return;
        }

        // 403 invalid_token は onTokenInvalid → トークン再取得 → layer.redraw() で
        // タイルが再生成されるため、ここでは done() を呼ばない（既存挙動を維持）。
        if (result.status === 403) {
          return;
        }

        // ネットワーク例外（リトライ尽き）や非403 HTTPエラーは src が未設定のままで
        // 終わる。何もしないと waitForMapReady が img.complete && naturalWidth > 0 を
        // 待ち続けて 10 秒タイムアウトに到達してしまうので、display:none で
        // 「対象外」扱いさせ、done(error, img) で Leaflet にも終了を通知する。
        img.style.display = "none";
        recordZenrinTileFailure();
        done(new Error("zenrin tile fetch failed"), img);
      } finally {
        this.off("tileunload", onTileUnload);
      }
    })();

    return img;
  }
}

interface ZenrinTileLayerInternalProps extends TileLayerProps {
  oauthToken?: string;
  setDisableOpenStreetMap?: React.Dispatch<React.SetStateAction<boolean>>;
  onTokenInvalid?: () => void;
}

/**
 * ゼンリン住宅地図を返すコンポーネント（内部実装）
 */
const ZenrinTileLayerInternal = createTileLayerComponent<
  TileLayerWithHeaders,
  ZenrinTileLayerInternalProps
>(
  function createTileLayer(
    {
      url,
      oauthToken,
      setDisableOpenStreetMap,
      onTokenInvalid,
      ...options
    }: ZenrinTileLayerInternalProps,
    context
  ) {
    // x-api-keyは必要に応じて残す（OAuthトークンが優先される）
    const apiKey = import.meta.env.VITE_ZENRIN_API_KEY as string | undefined;
    const headers = apiKey
      ? [
          {
            header: "x-api-key",
            value: apiKey,
          },
        ]
      : [];
    const layer = new TileLayerWithHeaders(
      url,
      withPane(options, context),
      headers,
      oauthToken,
      onTokenInvalid
    );

    // レイヤーが追加/削除された時にOpenStreetMapの表示を制御
    if (setDisableOpenStreetMap) {
      layer.on("add", () => {
        // 住宅地図レイヤーが有効の時はOpenStreetMapの読み込みを無効化する
        setDisableOpenStreetMap(true);
      });

      layer.on("remove", () => {
        setDisableOpenStreetMap(false);
      });
    }

    return createElementObject(layer, context);
  },
  function updateTileLayer(layer, props, prevProps) {
    updateGridLayer(layer, props, prevProps);
    const { url, oauthToken, onTokenInvalid } = props;
    if (url != null && url !== prevProps.url) {
      layer.setUrl(url);
    }
    if (oauthToken !== prevProps.oauthToken) {
      layer.setOAuthToken(oauthToken);
    }
    if (onTokenInvalid !== prevProps.onTokenInvalid) {
      layer.setOnTokenInvalid(onTokenInvalid);
    }
  }
);

interface ZenrinTileLayerProps extends TileLayerProps {
  oauthToken?: string;
  setDisableOpenStreetMap?: React.Dispatch<React.SetStateAction<boolean>>;
  canUseZenrinMap?: boolean;
}

/**
 * ゼンリン住宅地図を返すコンポーネント（OAuthトークン管理付き）
 */
export const ZenrinTileLayer: React.FC<ZenrinTileLayerProps> = ({
  canUseZenrinMap = true,
  setDisableOpenStreetMap,
  oauthToken,
  ...props
}) => {
  const { token: zenrinOAuthToken, refreshToken } = useZenrinOAuthToken(
    canUseZenrinMap && !oauthToken
  );
  const effectiveOAuthToken = oauthToken ?? zenrinOAuthToken;

  // トークンの再取得が連続で発生しないようにするためのref
  const isRefreshingRef = useRef(false);

  const handleTokenInvalid = useCallback(() => {
    // 既にリフレッシュ中の場合はスキップ
    if (isRefreshingRef.current) {
      return;
    }
    isRefreshingRef.current = true;

    console.warn("Zenrin OAuth token is invalid, refreshing...");
    void refreshToken().finally(() => {
      // リフレッシュ完了後、少し待ってからフラグをリセット
      setTimeout(() => {
        isRefreshingRef.current = false;
      }, 1000);
    });
  }, [refreshToken]);

  if (!canUseZenrinMap || !effectiveOAuthToken) {
    return null;
  }

  return (
    <ZenrinTileLayerInternal
      {...props}
      oauthToken={effectiveOAuthToken}
      setDisableOpenStreetMap={setDisableOpenStreetMap}
      onTokenInvalid={handleTokenInvalid}
    />
  );
};
